/**
 * Plays a live recording's HLS proxy while it grows: through MediaSource,
 * appending each new fMP4 segment as the playlist lists it (ManagedMediaSource
 * on iPhone), or the browser's own HLS player where MediaSource is missing. Starts at the live
 * edge (1.8.1: it fetches only the last three segments of a recording in progress); scrubbing back works over what
 * it has played, up to two minutes behind the playhead (older media leaves the buffer).
 */
import { useEffect, useRef, useState } from 'react';
import { Maximize, Pause, Play, Radio, Volume2, VolumeX } from 'lucide-react';
import type { LiveDTO } from '@prism/contracts';
import { PLAYER_EDGE_SECONDS, atLiveEdge, behindSeconds, evictionEnd, goLiveTarget, segmentsSeconds, segmentsToAppend } from './live-edge';
import { LiveEdgeButton } from './LiveEdgeButton';

/** 1.9.1: "Recording since 16:05:12" in the workspace's time zone (the recording's), not the browser's. */
export function sinceText(iso: string, timeZone?: string | null): string {
  const d = new Date(iso);
  try { return d.toLocaleTimeString('en-GB', { timeZone: timeZone || undefined, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }); } catch { return d.toLocaleTimeString(); }
}

const mediaSource = () => (window as unknown as { ManagedMediaSource?: typeof MediaSource }).ManagedMediaSource ?? window.MediaSource;

export function LivePlayer({ live, onTimeChange }: { live: LiveDTO; onTimeChange?: (s: number) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [buffered, setBuffered] = useState(0);
  const [time, setTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [scrub, setScrub] = useState<number | null>(null);
  // 1.9.1: seconds of the recording before the first segment appended (joined at the live edge); the player's own
  // time 0 is that point, so the recording's time is offset + currentTime.
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  const recording = live.state === 'recording';
  const recordingRef = useRef(recording);
  recordingRef.current = recording;

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    // MediaSource where there is one (it plays what has arrived at once); the built-in HLS player otherwise (older iPhones).
    if (!mediaSource() && v.canPlayType('application/vnd.apple.mpegurl')) {
      v.src = live.playlistUrl;
      return () => { v.removeAttribute('src'); v.load(); };
    }
    // MediaSource: fMP4 segments are appended as they appear in the playlist.
    const MS = mediaSource();
    if (!MS) { setError('This browser cannot play a live preview.'); return; }
    const ms = new MS();
    v.src = URL.createObjectURL(ms);
    let stopped = false;
    const abort = new AbortController();
    let joined = false;
    // 1.8.1: the first playlist read of a recording in progress joins at its live edge (its last segments, live-edge.ts);
    // play starts once those are appended.
    let caughtUp = false;
    let firstRead = true;
    offsetRef.current = 0;
    setOffset(0);
    let sb: SourceBuffer | null = null;
    // Segments appended (or skipped before the live edge). A segment is marked only once its fetch succeeded.
    const done = new Set<string>();
    const queue: ArrayBuffer[] = [];
    const pump = () => {
      if (stopped || !sb || sb.updating || ms.readyState !== 'open') return;
      // 1.8.1: media more than two minutes behind the playhead leaves the buffer first (sb.remove ends in updateend).
      const cut = v.buffered.length ? evictionEnd(v.currentTime, v.buffered.start(0)) : null;
      if (cut !== null) { sb.remove(v.buffered.start(0), cut); return; }
      if (queue.length) sb.appendBuffer(queue.shift()!);
    };
    const fetchBuf = async (name: string) => {
      const res = await fetch(new URL(name, new URL(live.playlistUrl, location.href)), { credentials: 'same-origin', signal: abort.signal });
      if (!res.ok) throw new Error(`The live preview could not load a segment (${res.status})`);
      return res.arrayBuffer();
    };
    const poll = async () => {
      while (!stopped) {
        try {
          const res = await fetch(live.playlistUrl, { credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
          if (!res.ok) throw new Error(`The live preview is not available (${res.status})`);
          const text = await res.text();
          if (stopped) return;
          if (!sb && /#EXT-X-MAP:URI="([^"]+)"/.test(text)) {
            const init = await fetchBuf(/#EXT-X-MAP:URI="([^"]+)"/.exec(text)![1]!);
            if (stopped) return;
            const codecs = MS.isTypeSupported('video/mp4; codecs="avc1.64001f,mp4a.40.2"') ? 'video/mp4; codecs="avc1.64001f,mp4a.40.2"' : 'video/mp4; codecs="avc1.4d401f,mp4a.40.2"';
            const buf = ms.addSourceBuffer(codecs);
            sb = buf;
            buf.mode = 'sequence';
            buf.addEventListener('updateend', () => {
              if (stopped) return;
              pump();
              if (!v.buffered.length) return;
              const end = v.buffered.end(v.buffered.length - 1);
              setBuffered(end);
              // Join a recording in progress at the live edge, once enough has loaded.
              if (!joined && caughtUp && !queue.length && !buf.updating && recordingRef.current) { joined = true; if (end > 4) v.currentTime = end - 3; void v.play().catch(() => undefined); }
            });
            queue.push(init);
          }
          if (sb) {
            const next = segmentsToAppend(text, done, { firstRead, recording: recordingRef.current });
            for (const name of next.skip) done.add(name);
            if (next.skip.length) { offsetRef.current = segmentsSeconds(text, next.skip); setOffset(offsetRef.current); }
            firstRead = false;
            for (const name of next.fetch) {
              const buf = await fetchBuf(name);
              if (stopped) return;
              done.add(name);
              queue.push(buf);
            }
            caughtUp = true;
            setError(null);
          }
          pump();
          if (text.includes('#EXT-X-ENDLIST') && caughtUp) {
            const end = () => { if (stopped) return; if (sb && !sb.updating && !queue.length && ms.readyState === 'open') ms.endOfStream(); else setTimeout(end, 300); };
            end();
            return;
          }
        } catch (err) {
          // A failed segment stays unmarked and is fetched again on the next read, in order.
          if (stopped) return;
          setError((err as Error).message);
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    ms.addEventListener('sourceopen', () => void poll(), { once: true });
    return () => {
      // 1.8.1: stops polling and fetching on unmount.
      stopped = true;
      abort.abort();
      queue.length = 0;
      URL.revokeObjectURL(v.src);
      v.removeAttribute('src');
      v.load();
    };
  }, [live.playlistUrl]);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    const t = () => { setTime(v.currentTime); onTimeChange?.(offsetRef.current + v.currentTime); if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1)); };
    const state = () => { setPlaying(!v.paused); setMuted(v.muted); };
    v.addEventListener('timeupdate', t);
    for (const e of ['play', 'pause', 'volumechange']) v.addEventListener(e, state);
    return () => { v.removeEventListener('timeupdate', t); for (const e of ['play', 'pause', 'volumechange']) v.removeEventListener(e, state); };
  }, [onTimeChange]);

  const behind = behindSeconds(buffered, time);
  const atEdge = recording && atLiveEdge(buffered, time, PLAYER_EDGE_SECONDS);
  const fmt = (s: number) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const v = video.current;
  const goLive = () => { if (v && v.buffered.length) { v.currentTime = goLiveTarget(v.buffered.end(v.buffered.length - 1)); void v.play().catch(() => undefined); } };
  // While recording the timeline is everything recorded so far: at the live edge the playhead sits at its right end.
  const position = scrub ?? (atEdge ? buffered : time);
  return (
    <div className="live-player" data-testid="live-player">
      <video ref={video} controls={!recording} playsInline autoPlay muted disableRemotePlayback onClick={() => { if (recording && v) void (v.paused ? v.play() : v.pause()); }} />
      {recording && <span className="live-badge recording" data-testid="live-recording"><Radio size={11} /> RECORDING</span>}
      {recording && (
        <div className="live-controls" data-testid="live-controls">
          <button className="live-ctl" aria-label={playing ? 'Pause' : 'Play'} onClick={() => v && void (v.paused ? v.play() : v.pause())}>{playing ? <Pause size={16} /> : <Play size={16} />}</button>
          <span className="live-time">{fmt(offset + position)}</span>
          <input className="live-scrub" type="range" min={0} max={Math.max(buffered, 0.1)} step={0.1} value={Math.min(position, buffered)} aria-label="Position in the recording"
            style={{ ['--pct' as string]: `${buffered ? (Math.min(position, buffered) / buffered) * 100 : 0}%` }}
            onChange={(e) => setScrub(Number(e.target.value))}
            onPointerUp={() => { if (v && scrub !== null) v.currentTime = scrub; setScrub(null); }}
            onKeyUp={() => { if (v && scrub !== null) v.currentTime = scrub; setScrub(null); }} />
          <LiveEdgeButton atEdge={atEdge} behind={behind} onGoLive={goLive} />
          <button className="live-ctl" aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => { if (v) v.muted = !v.muted; }}>{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
          <button className="live-ctl" aria-label="Full screen" onClick={() => void video.current?.parentElement?.requestFullscreen?.().catch(() => undefined)}><Maximize size={15} /></button>
        </div>
      )}
      <div className="live-bar">
        <span>{recording ? `Recording since ${sinceText(live.startedAt, live.timeZone)} · ${fmt(offset + buffered)} so far` : live.state === 'finishing' ? 'Recording ended; ingesting the finished file…' : `Recorded ${fmt(live.seconds ?? buffered)}`}</span>
        <span style={{ flex: 1 }} />
        {error && <span style={{ color: 'var(--danger)' }}>{error}</span>}
      </div>
    </div>
  );
}
